import { config } from "@config/config";
import { hasPayorSubmissionIntegration } from "@utils/payorConfig";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useEffect, useMemo } from "react";

import {
  ApiPayorReportSubmissionStatusChoices,
  ApiReportTemplatePayorTypeChoices,
  useGetPayorReportSubmissionStatusByReportIdQuery,
  useGetPayorReportSubmissionWorkflowStatusQuery,
  useStartPayorSubmissionWorkflowMutation,
} from "@api/graphql/types-and-hooks";

export interface UsePayorReportSubmissionParams {
  reportId: string | null;
  isReportCompleted: boolean;
  payorType: ApiReportTemplatePayorTypeChoices | null | undefined;
}

export const usePayorReportSubmission = ({
  reportId,
  isReportCompleted,
  payorType,
}: UsePayorReportSubmissionParams) => {
  const flags = useFlags();

  const isReportSubmitToPayorFlagEnabled = flags["report-submit-to-payor"];

  const isPayorWithSubmissionIntegration = hasPayorSubmissionIntegration(payorType);

  const isPayorSubmissionEnabled =
    isReportCompleted && isPayorWithSubmissionIntegration && !!reportId && isReportSubmitToPayorFlagEnabled;

  // Don't key on `isReportCompleted` — it's already gating `enabled` below, and
  // including it here invalidates the cache when the report flips Draft↔Completed
  // during an Edit→Complete cycle, causing the PayorSubmissionDialog to render
  // a frame of "Submit" copy before refetch returns NeedsRevision and the copy
  // snaps to "Resubmit." The underlying submission list doesn't change just
  // because reportStatus flips mid-edit, so the cache should survive the cycle.
  const payorQueryKey = useMemo(
    () => [reportId, isPayorWithSubmissionIntegration, isReportSubmitToPayorFlagEnabled],
    [reportId, isPayorWithSubmissionIntegration, isReportSubmitToPayorFlagEnabled],
  );

  const { isPending: isSubmittingToPayorInProgress, mutateAsync: startPayorSubmissionWorkflow } =
    useStartPayorSubmissionWorkflowMutation({});

  const {
    data: payorReportSubmissionStatusData,
    isLoading: isLoadingPayorReportSubmissionStatus,
    refetch: refetchPayorReportSubmissionStatus,
  } = useGetPayorReportSubmissionStatusByReportIdQuery(
    {
      reportId: reportId ?? "",
    },
    {
      enabled: isPayorSubmissionEnabled,
      queryKey: ["get-payor-report-submission-status-by-report-id", ...payorQueryKey],
    },
  );

  // Failure is an event, not a state — exclude FAILED rows so a previous
  // failed attempt can't masquerade as the current submission. Without this
  // filter, edges[0] for a report with prior FAILED rows is the failed one,
  // which (a) makes the lifecycle helper drop back to base reportStatus and
  // (b) points the workflow-status poll at the wrong jobId so the post-submit
  // useEffect false-positives "Submission failed" on the very next click.
  const payorSubmissionData = (
    payorReportSubmissionStatusData?.getPayorReportSubmissionsByReportId?.edges ?? []
  ).filter((edge) => edge?.node?.status !== ApiPayorReportSubmissionStatusChoices.Failed);
  const listPayorReportSubmissionStatus = payorSubmissionData[0]?.node?.status ?? null;
  const payorSubmissionJobId = payorSubmissionData[0]?.node?.jobId ?? null;

  const { data: payorReportSubmissionWorkflowData, refetch: refetchPayorReportSubmissionWorkflowStatus } =
    useGetPayorReportSubmissionWorkflowStatusQuery(
      {
        reportSubmissionId: payorSubmissionJobId ?? "<missing-report-submission-id>",
      },
      {
        enabled: isPayorSubmissionEnabled && payorSubmissionJobId !== null,
        queryKey: ["get-payor-report-submission-workflow-status", payorSubmissionJobId],
        retry: false,
        refetchInterval: (query) => {
          const status = query?.state?.data?.getPayorReportSubmissionStatus?.status;
          if (
            status === ApiPayorReportSubmissionStatusChoices.Completed ||
            status === ApiPayorReportSubmissionStatusChoices.Failed ||
            status === ApiPayorReportSubmissionStatusChoices.Approved ||
            status === ApiPayorReportSubmissionStatusChoices.NeedsRevision
          ) {
            return false;
          }
          return config.REPORT_POLLING_INTERVAL;
        },
      },
    );

  const workflowPayorReportSubmissionStatus =
    payorReportSubmissionWorkflowData?.getPayorReportSubmissionStatus?.status ?? null;

  // List query is not polled; workflow query is. Prefer workflow status whenever we have a job id so the UI
  // updates as polling runs (otherwise status stays stale until a full refetch / page refresh).
  const payorReportSubmissionStatus =
    payorSubmissionJobId !== null
      ? workflowPayorReportSubmissionStatus ?? listPayorReportSubmissionStatus
      : listPayorReportSubmissionStatus;

  useEffect(() => {
    if (!isPayorSubmissionEnabled || payorSubmissionJobId === null) return;
    if (
      listPayorReportSubmissionStatus !== ApiPayorReportSubmissionStatusChoices.Initialized &&
      listPayorReportSubmissionStatus !== ApiPayorReportSubmissionStatusChoices.InProgress
    ) {
      return;
    }
    void refetchPayorReportSubmissionWorkflowStatus();
  }, [
    isPayorSubmissionEnabled,
    listPayorReportSubmissionStatus,
    payorSubmissionJobId,
    refetchPayorReportSubmissionWorkflowStatus,
  ]);

  return {
    isPayorWithSubmissionIntegration,
    isPayorSubmissionEnabled,
    isSubmittingToPayorInProgress,
    isLoadingPayorReportSubmissionStatus,
    startPayorSubmissionWorkflow,
    payorReportSubmissionStatus,
    refetchPayorReportSubmissionStatus,
  };
};
